import { Link } from 'react-router-dom';
import type { Article } from '@/mocks/articles';

interface ArticleCardProps {
  article: Article;
  variant?: 'default' | 'compact' | 'featured';
}

export default function ArticleCard({ article, variant = 'default' }: ArticleCardProps) {
  const formatDate = (dateStr: string) => {
    const d = new Date(dateStr);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  };

  const formatViews = (count: number) => {
    if (count >= 10000) return `${(count / 10000).toFixed(1)}万`;
    return count.toString();
  };

  if (variant === 'compact') {
    return (
      <Link
        to={`/article/${article.id}`}
        className="group flex gap-3 p-3 rounded-lg hover:bg-background-100 transition-colors"
      >
        <div className="w-20 h-20 shrink-0 rounded-lg overflow-hidden">
          <img
            src={article.imageUrl}
            alt={article.title}
            className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300"
          />
        </div>
        <div className="flex-1 min-w-0">
          <h4 className="text-sm font-medium text-foreground-900 group-hover:text-primary-500 transition-colors line-clamp-2 leading-snug">
            {article.title}
          </h4>
          <div className="flex items-center gap-3 mt-1.5 text-xs text-foreground-500">
            <span>{formatDate(article.publishedAt)}</span>
            <span className="flex items-center gap-1">
              <i className="ri-eye-line"></i>
              {formatViews(article.viewCount)}
            </span>
          </div>
        </div>
      </Link>
    );
  }

  if (variant === 'featured') {
    return (
      <Link
        to={`/article/${article.id}`}
        className="group block relative rounded-xl overflow-hidden"
      >
        <div className="aspect-[4/3]">
          <img
            src={article.imageUrl}
            alt={article.title}
            className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
          />
        </div>
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent"></div>
        <div className="absolute inset-0 flex flex-col justify-end p-5">
          <span className="inline-block px-2.5 py-1 rounded-full text-xs font-medium bg-accent-500 text-white mb-2 w-fit">
            {article.categoryName}
          </span>
          <h3 className="text-lg font-heading font-bold text-white leading-snug line-clamp-2">
            {article.title}
          </h3>
          <p className="text-sm text-white/70 mt-1 line-clamp-1">{article.excerpt}</p>
          <div className="flex items-center gap-3 mt-2 text-xs text-white/60">
            <span>{formatDate(article.publishedAt)}</span>
            <span className="flex items-center gap-1">
              <i className="ri-eye-line"></i>
              {formatViews(article.viewCount)}
            </span>
            <span className="flex items-center gap-1">
              <i className="ri-chat-3-line"></i>
              {formatViews(article.commentCount)}
            </span>
          </div>
        </div>
      </Link>
    );
  }

  return (
    <Link
      to={`/article/${article.id}`}
      className="group block bg-background-50 rounded-xl overflow-hidden hover:bg-background-100 transition-all"
    >
      <div className="aspect-[16/10] overflow-hidden">
        <img
          src={article.imageUrl}
          alt={article.title}
          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
        />
      </div>
      <div className="p-4">
        <div className="flex items-center gap-2 mb-2">
          <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-accent-100 text-accent-700">
            {article.categoryName}
          </span>
          {article.isHot && (
            <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-primary-100 text-primary-600">
              热门
            </span>
          )}
        </div>
        <h3 className="text-base font-heading font-semibold text-foreground-900 group-hover:text-primary-500 transition-colors line-clamp-2 leading-snug">
          {article.title}
        </h3>
        <p className="text-sm text-foreground-600 mt-1.5 line-clamp-2 leading-relaxed">
          {article.excerpt}
        </p>
        <div className="flex items-center gap-3 mt-3 text-xs text-foreground-500">
          <span className="flex items-center gap-1">
            <i className="ri-calendar-line"></i>
            {formatDate(article.publishedAt)}
          </span>
          <span className="flex items-center gap-1">
            <i className="ri-eye-line"></i>
            {formatViews(article.viewCount)}
          </span>
        </div>
      </div>
    </Link>
  );
}